import { Anchor, Breadcrumbs, Loader } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { IconHome } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { DocumentFiles } from 'src/Components/Documents/DocumentFiles';
import { DocumentFolders } from 'src/Components/Documents/DocumentFolders';
import { DocumentFolderSuggestions } from 'src/Components/Documents/DocumentFolderSuggestions';
import { DocumentSelectionBanner } from 'src/Components/Documents/DocumentSelectionBanner';
import { DocumentSuggestionsBanner } from 'src/Components/Documents/DocumentSuggestionsBanner';
import { DocumentUploadHint } from 'src/Components/Documents/DocumentUploadHint';
import { DocumentSuggestionsModal } from 'src/Components/Modals/DocumentSuggestionsModal';
import { DocumentViewerModal } from 'src/Components/Modals/DocumentViewerModal';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { useTranslateFolderName } from 'src/Hooks/Format/useTranslateFolderName';
import { useCreateFileExport } from 'src/Hooks/Mutations/useCreateFileExport';
import { useDeleteStorageItem } from 'src/Hooks/Mutations/useDeleteStorageItem';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import useFetchDocuments from 'src/Hooks/Queries/useFetchDocuments';
import useFetchDocumentSuggestions from 'src/Hooks/Queries/useFetchDocumentSuggestions';
import { useFileDragState } from 'src/Hooks/Shared/useFileDragState';
import { useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import {
  AssetStorageItemDto,
  AssetStorageItemDtoTypeEnum,
  DocumentAnalysisStatus,
  VideoDto,
} from 'src/Services/API/generated';
import useDocumentPathStore from 'src/Stores/documentPathStore';

import styles from './DocumentBrowser.module.scss';

// t('documentSelection.exportName_one', 'Partage de {{count}} fichier')
// t('documentSelection.exportName_other', 'Partage de {{count}} fichiers')

const DocumentBrowser = () => {
  const { currentPath, setPath, goToParent } = useDocumentPathStore();
  const { data, isLoading } = useFetchDocuments(currentPath);
  const { mutate: deleteStorageItem } = useDeleteStorageItem();
  const [documentUrl, setDocumentUrl] = useState<string | undefined>(undefined);
  const [documentName, setDocumentName] = useState<string>('');
  const [documentVideo, setDocumentVideo] = useState<VideoDto | null>(null);
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<AssetStorageItemDto[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportName, setExportName] = useState('');
  const [exportedFileUrl, setExportedFileUrl] = useState('');

  const { mutate: createFileExport, isLoading: isCreatingExport } = useCreateFileExport({
    onSuccess: (data, variables) => {
      setExportedFileUrl(data.publicUrl);

      if (variables.length === 1) {
        const file = files?.find(f => f.id === variables[0]);
        setExportName(file?.name ?? '');
      } else {
        setExportName(t('documentSelection.exportName', { count: variables.length }));
      }

      setShowExportModal(true);
      setSelectedFiles([]);
    },
  });

  const { mutate: deleteMultipleStorageItems } = useDeleteStorageItem({
    onSuccess: () => {
      setSelectedFiles([]);
    },
  });

  const handleDeleteSelected = () => {
    selectedFiles.forEach(file => deleteMultipleStorageItems(file.id));
  };

  const handleShareSelected = () => {
    const ids = selectedFiles.map(file => file.id);
    createFileExport(ids);
  };

  const [documentModalOpen, { open: openDocumentModal, close: closeDocumentModal }] =
    useDisclosure();

  const { mutate: getPublicUrl } = useFetchStorageItemInformation({
    onSuccess: data => {
      if (data.externalUrl) {
        window.open(data.externalUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const url = data.urls?.previewLargeUrl ?? data.urls?.fileUrl ?? '';
      setDocumentUrl(url);
      setDocumentName(data.name);
      setDocumentVideo(data.video ?? null);
      openDocumentModal();
    },
  });

  // Deep link (Lucien's cards): ?path= opens a folder, &open= a file in it. Cleared once read so
  // the file doesn't open again on the next render.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const path = searchParams.get('path');
    const fileToOpen = searchParams.get('open');
    if (!path && !fileToOpen) return;
    if (path) setPath(path);
    if (fileToOpen) getPublicUrl(fileToOpen);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, setPath, getPublicUrl]);

  const { mutate: downloadFile } = useFetchStorageItemInformation({
    onSuccess: data => {
      if (data.urls?.fileUrl) {
        window.open(data.urls.fileUrl, '_blank');
      }
    },
  });
  const folders = useMemo(
    () =>
      data
        ?.filter(value => value.type === AssetStorageItemDtoTypeEnum.Folder)
        .map(value => ({ label: value.name, id: value.id })),
    [data]
  );

  const isMobile = useIsMobile();

  const files = useMemo(
    () => data?.filter(value => value.type !== AssetStorageItemDtoTypeEnum.Folder),
    [data]
  );

  const onFolderClick = useCallback(
    (folder: string) => {
      setPath(`${currentPath}${folder}/`);
    },
    [currentPath, setPath]
  );

  const { startUpload, isLoading: isUploading, uploads } = useFileUploadManager();
  const translateFolderName = useTranslateFolderName();
  const assetAboveQuotas = useIsAssetAboveQuotas();
  const isDraggingFiles = useFileDragState();

  const showUploadHint = !isLoading && (files?.length ?? 0) === 0;

  const { data: suggestions } = useFetchDocumentSuggestions();
  const [suggestionsModalOpen, { open: openSuggestionsModal, close: closeSuggestionsModal }] =
    useDisclosure();
  const pendingSuggestions = useMemo(
    () => suggestions?.filter(s => s.status === DocumentAnalysisStatus.Pending) ?? [],
    [suggestions]
  );
  const analyzingCount =
    suggestions?.filter(s => s.status === DocumentAnalysisStatus.Analyzing).length ?? 0;
  const suggestionStatusByFileId = useMemo(
    () => new Map(suggestions?.map(s => [s.fileId, s.status])),
    [suggestions]
  );

  return (
    <div className={styles.container}>
      <Breadcrumbs className={styles.folderPath}>
        <IconHome size={22} onClick={() => setPath('/')} className={styles.homeButton} />
        {currentPath
          .slice(1)
          .split('/')
          .map((folder, index, array) => {
            const pathToFolder = '/' + array.slice(0, index + 1).join('/');

            return (
              <Anchor
                key={index}
                onClick={() => setPath(pathToFolder)}
                style={{ cursor: 'pointer' }}
              >
                {translateFolderName(folder)}
              </Anchor>
            );
          })}
      </Breadcrumbs>
      <DocumentFolders
        folders={folders ?? []}
        onFolderClick={onFolderClick}
        onGoToParent={goToParent}
        showGoToParentFolder={currentPath !== '/'}
        onDeleteFolder={itemId => deleteStorageItem(itemId)}
        path={currentPath}
        onUploadToFolder={(folderName, droppedFiles) =>
          startUpload(droppedFiles, `${currentPath}${folderName}/`)
        }
        canUpload={!assetAboveQuotas}
      />
      {currentPath === '/' && !isLoading && (
        <DocumentFolderSuggestions
          existingFolderNames={folders?.map(folder => folder.label) ?? []}
          disabled={assetAboveQuotas}
        />
      )}
      <DocumentSuggestionsBanner
        pendingCount={pendingSuggestions.length}
        analyzingCount={analyzingCount}
        onOpen={openSuggestionsModal}
      />
      {selectedFiles.length > 0 && (
        <DocumentSelectionBanner
          count={selectedFiles.length}
          onDeleteClick={handleDeleteSelected}
          onShareClick={handleShareSelected}
          shareLoading={isCreatingExport}
        />
      )}
      <Dropzone
        onDrop={droppedFiles => startUpload(droppedFiles, currentPath)}
        loading={isUploading}
        onReject={rejected => console.error('rejected files', rejected)}
        activateOnClick={false}
        enablePointerEvents
        disabled={assetAboveQuotas}
        className={`${styles.dropzone} ${isDraggingFiles ? styles.dropzoneDragging : ''}`}
      >
        {showUploadHint ? (
          <DocumentUploadHint isDragging={isDraggingFiles} />
        ) : (
          <DocumentFiles
            files={files ?? []}
            loading={isLoading}
            compact={isMobile}
            onDeleteFile={itemId => deleteStorageItem(itemId)}
            onFileClick={fileId => getPublicUrl(fileId)}
            onDownloadFile={fileId => downloadFile(fileId)}
            path={currentPath}
            selectedRecords={selectedFiles}
            onSelectedRecordsChange={setSelectedFiles}
            suggestionStatusByFileId={suggestionStatusByFileId}
            onSuggestionClick={openSuggestionsModal}
          />
        )}
      </Dropzone>
      {Array.from(uploads)
        .filter(upload => upload.status === 'processing')
        .map(upload => (
          <div className={styles.row}>
            <Loader size={16} color="blue" />
            <div key={upload.localId}>
              {`${t('documentFiles.file', 'Fichier')} ${upload.file.name} ${t('documentFiles.converting', 'en cours de traitement...')}`}
            </div>
          </div>
        ))}
      <QRCodeModal
        open={showExportModal}
        url={exportedFileUrl}
        onClose={() => setShowExportModal(false)}
        exportName={exportName}
      />
      <DocumentSuggestionsModal
        open={suggestionsModalOpen}
        onClose={closeSuggestionsModal}
        suggestions={pendingSuggestions}
      />
      <DocumentViewerModal
        open={documentModalOpen}
        fileUrl={documentUrl ?? ''}
        fileName={documentName}
        video={documentVideo}
        onClose={closeDocumentModal}
      />
    </div>
  );
};

export default DocumentBrowser;
